// FILE: src/pages/CashAccountsPage.jsx
//
// Cash Accounts page — UI redesign (presentation only).
//
//   - Geist / Geist Mono typography, CSS fully scoped under `.capg-root`
//   - Sticky page-top (title + subtitle + Add Account) with a filter bar
//     (search, sort, archived toggle) underneath
//   - Responsive account card grid: name, mono account no, big tabular
//     balance (red when negative, green when positive), status chips
//     (Locked Until / ReOpen Mode / Needs Reconciliation / Archived),
//     3-dot row menu, dimmed+grayscale archived cards
//   - Add / Edit dialogs re-skinned but powered by MUI Dialog underneath
//     (Add keeps its native <form> submit so required-field validation
//     behaves exactly as before)
//   - Inline Alert feedback converted to bottom-center Snackbar + Alert
//     (autoHide 4000) — same messages, same severities, including the
//     server's duplicate-accountNo `warning` responses
//
// All data behavior preserved byte-for-byte:
//   GET  /accounts?limit=100000000   (params: { search })
//   GET  /users/me                   (currency preference)
//   POST /accounts/create            ({ accountNo, accountName, description })
//   PUT  /accounts/:id               (edit fields / { isActive } toggle)
//   navigate(`/account/detail/${accId}`) on card click.

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";

// Material UI (kept underneath for behavior/accessibility)
import {
  Dialog,
  DialogContent,
  Snackbar,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Fab,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";

// Import the centralized money formatting utility
import { formatMoney } from "../utils/formatMoney";

// Helper to format date
const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString();
};

// Check if lockedUpToDate is older than 7 days
const needsReconciliation = (acc) => {
  if (!acc.lockedUpToDate) return false;
  const lockedDate = new Date(acc.lockedUpToDate);
  const now = new Date();
  const diff = now - lockedDate; // ms difference
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return diff > sevenDays;
};

// Presentation-only: balance color class (negative red, positive green, zero gray)
const balanceClass = (balance) => {
  if (balance < 0) return "is-neg";
  if (balance > 0) return "is-pos";
  return "is-zero";
};

// ----------------------------------------
// CSS — fully scoped under `.capg-root`.
// ----------------------------------------
const CASH_ACCOUNTS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

/* Dialog/menu papers reuse .capg-root for tokens — never inherit page sizing */
.MuiPaper-root.capg-root {
  min-height: 0;
  background: #fff;
}
.capg-root {
  --capg-primary: #1E90FF;
  --capg-primary-soft: #E8F3FF;
  --capg-warn: #FFC107;
  --capg-warn-soft: #FFF7DC;
  --capg-warn-deep: #B86E00;
  --capg-success: #2E7D32;
  --capg-success-soft: #E8F5E9;
  --capg-danger: #C62828;
  --capg-danger-soft: #FDECEC;
  --capg-text-1: #111827;
  --capg-text-2: #374151;
  --capg-text-3: #6B7280;
  --capg-text-4: #9CA3AF;
  --capg-border-1: #ECECEC;
  --capg-border-2: #DDDDDD;
  --capg-surface: #FFFFFF;
  --capg-surface-sub: #FAFAFA;
  --capg-surface-sub-2: #F4F4F5;
  --capg-radius-sm: 6px;
  --capg-radius-md: 8px;
  --capg-radius-lg: 12px;
  --capg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --capg-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --capg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--capg-text-1);
  background: var(--capg-surface-sub);
  min-height: 100vh;
  padding: 0;
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.capg-root *, .capg-root *::before, .capg-root *::after { box-sizing: border-box; }

/* ----- Sticky top region (page header + filter bar) ----- */
.capg-sticky-top {
  position: sticky;
  top: var(--capg-navbar-h);
  z-index: 30;
  background: var(--capg-surface-sub);
  border-bottom: 1px solid var(--capg-border-1);
}
.capg-header {
  padding: 20px 24px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.capg-title-block { min-width: 0; flex: 1 1 320px; }
.capg-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
  color: var(--capg-text-1);
}
.capg-subtitle {
  display: flex; align-items: center; gap: 10px;
  font-size: 12.5px; color: var(--capg-text-3); flex-wrap: wrap;
}
.capg-subtitle b { font-weight: 600; color: var(--capg-text-2); }
.capg-subtitle-dot { width: 4px; height: 4px; border-radius: 50%; background: var(--capg-text-4); }
.capg-subtitle-mono {
  font-family: 'Geist Mono', monospace; font-size: 11.5px;
  font-variant-numeric: tabular-nums;
}

/* ----- Buttons ----- */
.capg-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.capg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--capg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  transform: none;
}
.capg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.capg-btn-primary { background: var(--capg-primary); color: #fff; }
.capg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.capg-btn-ghost {
  background: var(--capg-surface); color: var(--capg-text-2);
  border-color: var(--capg-border-2);
}
.capg-btn-ghost:hover:not(:disabled) { background: var(--capg-surface-sub-2); }
.capg-btn-toggle.is-on {
  background: var(--capg-primary-soft); color: #0E5EAB;
  border-color: rgba(30,144,255,0.35);
}
.capg-btn-toggle.is-on:hover:not(:disabled) { background: #DCEDFF; }
.capg-toggle-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--capg-text-4);
}
.capg-btn-toggle.is-on .capg-toggle-dot { background: var(--capg-primary); }

/* ----- Filter bar ----- */
.capg-filterbar {
  display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap;
  padding: 14px 24px 16px;
  background: var(--capg-surface);
  border-top: 1px solid var(--capg-border-1);
}
.capg-filter-field { display: flex; flex-direction: column; gap: 4px; }
.capg-filter-field label {
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--capg-text-3);
}
.capg-input, .capg-select {
  height: 34px; padding: 0 10px;
  background: var(--capg-surface);
  border: 1px solid var(--capg-border-2);
  border-radius: var(--capg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--capg-text-1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  min-width: 150px;
}
.capg-input:focus, .capg-select:focus {
  outline: none;
  border-color: var(--capg-primary);
  box-shadow: 0 0 0 3px var(--capg-primary-soft);
}
.capg-input-search {
  background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 16 16' xmlns='http://www.w3.org/2000/svg' fill='none'%3E%3Ccircle cx='7' cy='7' r='4.5' stroke='%236B7280' stroke-width='1.4'/%3E%3Cpath d='M10.5 10.5l3 3' stroke='%236B7280' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 10px center;
  padding-left: 30px;
  min-width: 220px;
}

/* ----- Body region ----- */
.capg-body { padding: 18px 24px 32px; }

/* ----- Account card grid ----- */
.capg-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}
.capg-card {
  position: relative;
  display: flex; flex-direction: column; gap: 10px;
  background: var(--capg-surface);
  border: 1px solid var(--capg-border-1);
  border-radius: var(--capg-radius-lg);
  box-shadow: var(--capg-shadow-sm);
  padding: 16px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
}
.capg-card:hover {
  box-shadow: var(--capg-shadow-md);
  border-color: var(--capg-border-2);
  transform: translateY(-1px);
}
.capg-card.is-archived {
  opacity: 0.55;
  filter: grayscale(100%);
}
.capg-card-top { min-width: 0; padding-right: 30px; }
.capg-card-name {
  font-size: 14px; font-weight: 600; color: var(--capg-text-1);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.capg-card-no {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 11px; color: var(--capg-text-3);
  margin-top: 1px;
}
.capg-card-balance-lab {
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.05em; color: var(--capg-text-3);
  margin-bottom: 2px;
}
.capg-card-balance {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 22px; font-weight: 600; line-height: 1.2;
  color: var(--capg-text-1);
}
.capg-card-balance.is-neg { color: var(--capg-danger); }
.capg-card-balance.is-pos { color: var(--capg-success); }
.capg-card-balance.is-zero { color: var(--capg-text-3); }
.capg-card-desc {
  font-size: 12px; color: var(--capg-text-3); line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.capg-card-chips {
  display: flex; gap: 6px; flex-wrap: wrap;
  margin-top: auto;
}

/* 3-dot row menu trigger */
.capg-card-dots {
  position: absolute; top: 10px; right: 10px;
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--capg-radius-sm);
  color: var(--capg-text-3);
  cursor: pointer;
  transition: all 0.12s ease;
}
.capg-card-dots:hover {
  background: var(--capg-surface-sub-2);
  color: var(--capg-text-1);
  border-color: var(--capg-border-2);
}

/* ----- Status chips ----- */
.capg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px 2px 6px;
  font-size: 11px; font-weight: 500;
  border-radius: 999px;
  white-space: nowrap;
}
.capg-chip-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
.capg-chip-warn {
  background: var(--capg-warn-soft); color: var(--capg-warn-deep);
  border: 1px solid #F1D88A;
}
.capg-chip-warn .capg-chip-dot { background: var(--capg-warn-deep); }
.capg-chip-success {
  background: var(--capg-success-soft); color: var(--capg-success);
  border: 1px solid #B7DBBA;
}
.capg-chip-success .capg-chip-dot { background: var(--capg-success); }
.capg-chip-danger {
  background: var(--capg-danger-soft); color: var(--capg-danger);
  border: 1px solid #F4C7C7;
}
.capg-chip-danger .capg-chip-dot { background: var(--capg-danger); }
.capg-chip-neutral {
  background: var(--capg-surface-sub-2); color: var(--capg-text-3);
  border: 1px solid var(--capg-border-2);
}
.capg-chip-neutral .capg-chip-dot { background: var(--capg-text-4); }
.capg-chip-mono { font-family: 'Geist Mono', monospace; font-variant-numeric: tabular-nums; }

/* ----- Empty / Loading states ----- */
.capg-state {
  padding: 56px 32px;
  text-align: center;
  color: var(--capg-text-3);
  background: var(--capg-surface);
  border: 1px solid var(--capg-border-1);
  border-radius: var(--capg-radius-lg);
  box-shadow: var(--capg-shadow-sm);
}
.capg-state-icon {
  margin: 0 auto 12px;
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--capg-surface-sub-2);
  color: var(--capg-text-3);
}
.capg-state-title { color: var(--capg-text-2); font-size: 14px; font-weight: 500; margin-bottom: 4px; }
.capg-state-sub { font-size: 12.5px; color: var(--capg-text-3); max-width: 360px; margin: 0 auto; }
.capg-state-loading {
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}

/* ----- Modal skinning (MUI Dialog underneath, custom look) ----- */
.capg-dlg .MuiPaper-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  border-radius: var(--capg-radius-lg) !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18) !important;
  overflow: hidden;
}
.capg-dlg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--capg-border-1);
}
.capg-dlg-title { font-size: 16px; font-weight: 600; color: var(--capg-text-1); margin: 0; }
.capg-dlg-sub { font-size: 12px; color: var(--capg-text-3); margin-top: 3px; }
.capg-dlg-close {
  background: none; border: none; color: var(--capg-text-3);
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.capg-dlg-close:hover { background: var(--capg-surface-sub-2); color: var(--capg-text-1); }
.capg-dlg-body { padding: 18px 20px; }
.capg-dlg-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--capg-border-1);
  background: var(--capg-surface-sub);
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px;
}

/* Neutralize global styles.css bare <form> styling inside our dialogs */
.capg-root .capg-dlg-form { display: block; margin: 0; gap: 0; }

/* Form field bits inside dialogs */
.capg-flab {
  display: block; font-size: 11px; font-weight: 500;
  color: var(--capg-text-2); letter-spacing: 0.02em;
  text-transform: uppercase; margin-bottom: 4px;
}
.capg-flab-req::after { content: ' *'; color: var(--capg-primary); font-weight: 700; }
.capg-frow { margin-bottom: 14px; }
.capg-fhint { font-size: 11px; color: var(--capg-text-3); margin-top: 3px; }
.capg-root .capg-finput {
  width: 100%; height: 34px; padding: 0 10px;
  border: 1px solid var(--capg-border-2);
  border-radius: var(--capg-radius-md);
  font-family: inherit; font-size: 13px;
  color: var(--capg-text-1); background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.capg-root .capg-finput:focus {
  outline: none; border-color: var(--capg-primary);
  box-shadow: 0 0 0 3px var(--capg-primary-soft);
}
.capg-finput-mono { font-family: 'Geist Mono', monospace !important; font-size: 12.5px !important; font-variant-numeric: tabular-nums; }
.capg-root .capg-ftextarea {
  width: 100%; min-height: 64px; padding: 8px 10px; resize: vertical;
  border: 1px solid var(--capg-border-2); border-radius: var(--capg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--capg-text-1);
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.capg-root .capg-ftextarea:focus {
  outline: none; border-color: var(--capg-primary);
  box-shadow: 0 0 0 3px var(--capg-primary-soft);
}

/* ----- Row menu (rendered in a portal, tokens via .capg-root on Paper) ----- */
.capg-menu-paper {
  border-radius: var(--capg-radius-md) !important;
  border: 1px solid var(--capg-border-1) !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12) !important;
  min-width: 160px !important;
  min-height: 0 !important;
}
.capg-menu-paper .MuiMenuItem-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 13px;
  color: var(--capg-text-2);
  min-height: 34px;
}
.capg-menu-paper .MuiMenuItem-root:hover { background: var(--capg-surface-sub-2); }

/* ----- Floating FAB wrapper (small screens only) ----- */
.capg-fab-wrap {
  position: fixed; bottom: 24px; right: 24px;
  display: none; z-index: 40;
}
@media (max-width: 899.95px) {
  .capg-fab-wrap { display: block; }
}
`;

// ----------------------------------------
// Small inline icons
// ----------------------------------------
function IconPlus({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
function IconClose({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconDots({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="13" cy="8" r="1.4" />
    </svg>
  );
}
function IconWallet({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 10h18" stroke="currentColor" strokeWidth="1.5" />
      <path d="M7 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function CashAccountsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Account data
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // Alerts (surfaced through the bottom-center Snackbar)
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // "Add" dialog states
  const [openAdd, setOpenAdd] = useState(false);
  const [accountNo, setAccountNo] = useState("");
  const [accountName, setAccountName] = useState("");
  const [description, setDescription] = useState("");

  // Edit dialog states
  const [openEdit, setOpenEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editAccountNo, setEditAccountNo] = useState("");
  const [editAccountName, setEditAccountName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  // "More" menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuAccountId, setMenuAccountId] = useState(null);

  // FEATURE: Filtering / Sorting
  const [showArchived, setShowArchived] = useState(false); // Hide archived by default
  const [sortBy, setSortBy] = useState(""); // "", "balance", "createdAt", or "accountName"

  // New state: user's preferred currency; default is USD
  const [currency, setCurrency] = useState("USD");

  // ─────────────────────────────────────────────────────────────────
  // 1) fetch accounts => show both active + archived
  // Then do local filtering/sorting
  // ─────────────────────────────────────────────────────────────────
  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get("/accounts?limit=100000000", { params: { search } });
      let accs = response.data.accounts || [];

      // Filter out archived if showArchived == false
      if (!showArchived) {
        accs = accs.filter((a) => a.isActive);
      }

      // Sort by highest balance, newest creation date, or accountName
      if (sortBy === "balance") {
        accs.sort((a, b) => (b.balance || 0) - (a.balance || 0));
      } else if (sortBy === "createdAt") {
        accs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      } else if (sortBy === "accountName") {
        accs.sort((a, b) => a.accountName.localeCompare(b.accountName));
      }
      // no sort if sortBy === ""

      setAccounts(accs);
    } catch (err) {
      console.error("Error fetching accounts:", err);
      setAlertMessage("Failed to load accounts");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  }, [search, showArchived, sortBy]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Fetch user currency preference from /users/me
  useEffect(() => {
    async function fetchCurrency() {
      try {
        const res = await API.get("/users/me");
        setCurrency(res.data.currency || "USD");
      } catch (err) {
        console.error("Error fetching currency:", err);
      }
    }
    fetchCurrency();
  }, []);

  // Navigate to detail
  const handleCardClick = (accId) => {
    navigate(`/account/detail/${accId}`);
  };

  // Add account
  const handleAddAccount = async (e) => {
    e.preventDefault();
    try {
      const res = await API.post("/accounts/create", {
        accountNo,
        accountName,
        description,
      });

      if (res.data.warning) {
        setAlertMessage(res.data.warning);
        setAlertSeverity("warning");
      } else {
        setAlertMessage(res.data.message || "Account created successfully!");
        setAlertSeverity("success");
      }

      setOpenAdd(false);
      setAccountNo("");
      setAccountName("");
      setDescription("");

      fetchAccounts();
    } catch (error) {
      console.error("Add account error:", error);
      setAlertMessage(error.response?.data?.message || "Failed to add account");
      setAlertSeverity("error");
    }
  };

  // "More" menu
  const handleMoreClose = () => {
    setAnchorEl(null);
    setMenuAccountId(null);
  };

  // Edit
  const handleEditAccount = () => {
    handleMoreClose();
    const acc = accounts.find((a) => a._id === menuAccountId);
    if (!acc) return;
    setEditId(acc._id);
    setEditAccountNo(acc.accountNo || "");
    setEditAccountName(acc.accountName || "");
    setEditDescription(acc.description || "");
    setOpenEdit(true);
  };

  const handleEditSave = async () => {
    try {
      if (!editId) return;
      const res = await API.put(`/accounts/${editId}`, {
        accountNo: editAccountNo,
        accountName: editAccountName,
        description: editDescription,
      });

      if (res.data.warning) {
        setAlertMessage(res.data.warning);
        setAlertSeverity("warning");
      } else {
        setAlertMessage(res.data.message || "Account updated successfully!");
        setAlertSeverity("success");
      }

      setOpenEdit(false);
      fetchAccounts();
    } catch (error) {
      console.error("Edit account error:", error);
      setAlertMessage(error.response?.data?.message || "Failed to edit account");
      setAlertSeverity("error");
    }
  };

  // Archive / Unarchive
  const handleToggleArchive = async () => {
    handleMoreClose();
    try {
      if (!menuAccountId) return;
      const acc = accounts.find((a) => a._id === menuAccountId);
      if (!acc) return;

      const newIsActive = !acc.isActive;
      await API.put(`/accounts/${menuAccountId}`, {
        isActive: newIsActive,
      });

      setAlertMessage(
        newIsActive
          ? "Account unarchived successfully!"
          : "Account archived successfully!"
      );
      setAlertSeverity("success");
      fetchAccounts();
    } catch (error) {
      console.error("Archive/unarchive error:", error);
      setAlertMessage(
        error.response?.data?.message || "Failed to toggle archive"
      );
      setAlertSeverity("error");
    }
  };

  return (
    <div className="capg-root">
      <style>{CASH_ACCOUNTS_CSS}</style>

      {/* Sticky page-top */}
      <div className="capg-sticky-top">
        <div className="capg-header">
          <div className="capg-title-block">
            <h1 className="capg-title">Cash Accounts</h1>
            <div className="capg-subtitle">
              {!loading && (
                <>
                  <span>
                    <b>{accounts.length}</b>{" "}
                    {accounts.length === 1 ? "account" : "accounts"}
                    {showArchived ? " (incl. archived)" : ""}
                  </span>
                  <span className="capg-subtitle-dot" />
                </>
              )}
              <span className="capg-subtitle-mono">Balances in {currency}</span>
            </div>
          </div>

          {/* Action bar */}
          <div className="capg-actions">
            <button
              type="button"
              className="capg-btn capg-btn-primary"
              onClick={() => setOpenAdd(true)}
            >
              <IconPlus /> Add Account
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="capg-filterbar">
          <div className="capg-filter-field" style={{ flex: "1 1 220px", minWidth: 220 }}>
            <label htmlFor="capg-search">Search</label>
            <input
              id="capg-search"
              className="capg-input capg-input-search"
              type="text"
              placeholder="Search accounts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: "100%" }}
            />
          </div>

          <div className="capg-filter-field">
            <label htmlFor="capg-sort">Sort by</label>
            <select
              id="capg-sort"
              className="capg-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="">None</option>
              <option value="balance">Balance (High-Low)</option>
              <option value="createdAt">Newest Created</option>
              <option value="accountName">Account Name A-Z</option>
            </select>
          </div>

          <div className="capg-filter-field">
            <label htmlFor="capg-archived">Archived</label>
            <button
              id="capg-archived"
              type="button"
              className={`capg-btn capg-btn-ghost capg-btn-toggle${showArchived ? " is-on" : ""}`}
              onClick={() => setShowArchived(!showArchived)}
            >
              <span className="capg-toggle-dot" />
              {showArchived ? "Showing Archived" : "Hide Archived"}
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="capg-body">
        {loading ? (
          <div className="capg-state">
            <div className="capg-state-loading">
              <CircularProgress size={28} />
              <div className="capg-state-sub">Loading accounts…</div>
            </div>
          </div>
        ) : accounts.length > 0 ? (
          <div className="capg-grid">
            {accounts.map((acc) => {
              const isArchived = !acc.isActive;
              const showNeedsRecon = needsReconciliation(acc);
              const hasChips =
                isArchived ||
                acc.lockedUpToDate ||
                acc.inReOpenMode ||
                showNeedsRecon;

              return (
                <div
                  key={acc._id}
                  className={`capg-card${isArchived ? " is-archived" : ""}`}
                  onClick={() => handleCardClick(acc._id)}
                >
                  {/* "More" menu button */}
                  <button
                    type="button"
                    className="capg-card-dots"
                    aria-label="Account actions"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnchorEl(e.currentTarget);
                      setMenuAccountId(acc._id);
                    }}
                  >
                    <IconDots />
                  </button>

                  <div className="capg-card-top">
                    <div className="capg-card-name" title={acc.accountName}>
                      {acc.accountName}
                    </div>
                    <div className="capg-card-no">#{acc.accountNo}</div>
                  </div>

                  <div>
                    <div className="capg-card-balance-lab">Balance</div>
                    <div className={`capg-card-balance ${balanceClass(acc.balance)}`}>
                      {formatMoney(acc.balance, currency)}
                    </div>
                  </div>

                  {acc.description && (
                    <div className="capg-card-desc" title={acc.description}>
                      {acc.description}
                    </div>
                  )}

                  {/* Lock/Reopen + Recon info */}
                  {hasChips && (
                    <div className="capg-card-chips">
                      {isArchived && (
                        <span className="capg-chip capg-chip-neutral">
                          <span className="capg-chip-dot" />
                          Archived
                        </span>
                      )}
                      {acc.lockedUpToDate && (
                        <span className="capg-chip capg-chip-warn capg-chip-mono">
                          <span className="capg-chip-dot" />
                          Locked Until {fmtDate(acc.lockedUpToDate)}
                        </span>
                      )}
                      {acc.inReOpenMode && (
                        <span className="capg-chip capg-chip-success">
                          <span className="capg-chip-dot" />
                          ReOpen Mode
                        </span>
                      )}
                      {showNeedsRecon && (
                        <span className="capg-chip capg-chip-danger">
                          <span className="capg-chip-dot" />
                          Needs Reconciliation
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="capg-state">
            <div className="capg-state-icon">
              <IconWallet />
            </div>
            <div className="capg-state-title">No accounts found.</div>
            <div className="capg-state-sub">
              {search
                ? "Try a different search term, or clear the search to see all accounts."
                : "Add your first cash account to start tracking balances."}
            </div>
          </div>
        )}
      </div>

      {/* Menu for "Edit" or "Archive/Unarchive" */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
          setMenuAccountId(null);
        }}
        PaperProps={{ className: "capg-root capg-menu-paper" }}
      >
        <MenuItem onClick={handleEditAccount}>Edit</MenuItem>
        <MenuItem onClick={handleToggleArchive}>
          {(() => {
            const found = accounts.find((a) => a._id === menuAccountId);
            return found && !found.isActive ? "Unarchive" : "Archive";
          })()}
        </MenuItem>
      </Menu>

      {/* Snackbar feedback (success / warning / error) */}
      <Snackbar
        open={Boolean(alertMessage)}
        autoHideDuration={4000}
        onClose={() => setAlertMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertMessage("")}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Add Account Dialog */}
      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmallScreen}
        className="capg-dlg"
        PaperProps={{ className: "capg-root" }}
      >
        <style>{CASH_ACCOUNTS_CSS}</style>
        <div className="capg-dlg-head">
          <div>
            <div className="capg-dlg-title">Add New Cash Account</div>
            <div className="capg-dlg-sub">
              Create an account to track a till, safe, or bank float.
            </div>
          </div>
          <button
            type="button"
            className="capg-dlg-close"
            onClick={() => setOpenAdd(false)}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        {/* Native <form> kept so required-field validation behaves as before */}
        <form className="capg-dlg-form" onSubmit={handleAddAccount}>
          <DialogContent className="capg-dlg-body">
            <div className="capg-frow">
              <label className="capg-flab capg-flab-req" htmlFor="capg-add-no">
                Account No.
              </label>
              <input
                id="capg-add-no"
                className="capg-finput capg-finput-mono"
                type="text"
                required
                value={accountNo}
                onChange={(e) => setAccountNo(e.target.value)}
              />
            </div>
            <div className="capg-frow">
              <label className="capg-flab capg-flab-req" htmlFor="capg-add-name">
                Account Name
              </label>
              <input
                id="capg-add-name"
                className="capg-finput"
                type="text"
                required
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
              />
            </div>
            <div className="capg-frow">
              <label className="capg-flab" htmlFor="capg-add-desc">
                Description (Optional)
              </label>
              <textarea
                id="capg-add-desc"
                className="capg-ftextarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </DialogContent>
          <div className="capg-dlg-foot">
            <button
              type="button"
              className="capg-btn capg-btn-ghost"
              onClick={() => setOpenAdd(false)}
            >
              Cancel
            </button>
            <button type="submit" className="capg-btn capg-btn-primary">
              <IconPlus /> Add Account
            </button>
          </div>
        </form>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmallScreen}
        className="capg-dlg"
        PaperProps={{ className: "capg-root" }}
      >
        <style>{CASH_ACCOUNTS_CSS}</style>
        <div className="capg-dlg-head">
          <div>
            <div className="capg-dlg-title">Edit Cash Account</div>
            <div className="capg-dlg-sub">
              Update the account's number, name, or description.
            </div>
          </div>
          <button
            type="button"
            className="capg-dlg-close"
            onClick={() => setOpenEdit(false)}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent className="capg-dlg-body">
          <div className="capg-frow">
            <label className="capg-flab capg-flab-req" htmlFor="capg-edit-no">
              Account No.
            </label>
            <input
              id="capg-edit-no"
              className="capg-finput capg-finput-mono"
              type="text"
              required
              value={editAccountNo}
              onChange={(e) => setEditAccountNo(e.target.value)}
            />
          </div>
          <div className="capg-frow">
            <label className="capg-flab capg-flab-req" htmlFor="capg-edit-name">
              Account Name
            </label>
            <input
              id="capg-edit-name"
              className="capg-finput"
              type="text"
              required
              value={editAccountName}
              onChange={(e) => setEditAccountName(e.target.value)}
            />
          </div>
          <div className="capg-frow">
            <label className="capg-flab" htmlFor="capg-edit-desc">
              Description (Optional)
            </label>
            <textarea
              id="capg-edit-desc"
              className="capg-ftextarea"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
          </div>
        </DialogContent>
        <div className="capg-dlg-foot">
          <button
            type="button"
            className="capg-btn capg-btn-ghost"
            onClick={() => setOpenEdit(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="capg-btn capg-btn-primary"
            onClick={handleEditSave}
          >
            Save Changes
          </button>
        </div>
      </Dialog>

      {/* Floating FAB on small screens */}
      <div className="capg-fab-wrap">
        <Fab
          color="primary"
          onClick={() => setOpenAdd(true)}
          sx={{ bgcolor: "#1E90FF", "&:hover": { bgcolor: "#1A7DD9" } }}
        >
          <AddIcon />
        </Fab>
      </div>
    </div>
  );
}

export default CashAccountsPage;
